import {
  OrderApi,
  useChangeOrderStatusMutation,
  useGetOrderByIdQuery,
} from "@/redux/services/order/order.api";
import { useGetOrderPaymentsQuery } from "@/redux/services/pos/payment.api";
import OrderItemDetail from "./OrderItemDetail";
import { ComboOrderItem } from "./ComboOrderItem";
import { FormulaOrderItem } from "./FormulaOrderItem";
import { OrderStatus } from "@/redux/services/order/order.type";
import {
  displayPrice,
  formatDate,
  formatPrice,
  getOrderActionText,
  getOrderStatusText,
  getOrderTotal,
  getOrderTypeLabel,
  getOrderSourceLabel,
  isWithinEditWindow,
  STATUS_BUTTON_STYLES,
} from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import {
  ORDERS_MANAGE_PERMISSIONS,
  POS_PERMISSIONS,
  isKitchenOnly,
} from "@/lib/navigation";
import { logger } from "@/services/Logger";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  X,
  CreditCard,
  Clock,
  Loader2,
  Pencil,
  Receipt,
  GitMerge,
  PencilLine,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PaymentModal from "@/components/pos/payment/PaymentModal";
import MergeTableAction from "./MergeTableAction";
import ReceiptPreview from "@/components/pos/receipt/ReceiptPreview";
import PaymentHistory from "@/components/pos/orders/PaymentHistory";
import RecordMovementModal from "@/components/pos/register/RecordMovementModal";
import { useOrderCloseConfirm } from "@/hooks/useOrderCloseConfirm";
import { getOutstandingRefund } from "@/lib/refunds";
import {
  getUnrefundedAmount,
  isCollectedMoneyViolation,
  useCollectedMoneyCancelBlock,
} from "@/hooks/useCollectedMoneyCancelBlock";
import { hasApiErrorKey, toastApiError } from "@/lib/apiErrors";

const OrderDetailsDrawer: React.FC<{
  orderId: string;
  orderNumber?: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ orderId, orderNumber, isOpen, onClose }) => {
  const {
    data: order,
    isLoading,
    isError,
    refetch,
  } = useGetOrderByIdQuery(
    { id: orderId },
    // Several screens keep this drawer mounted with an empty id while it is
    // closed; without the skip that fires GET /api/orders/ (403) on every view.
    { skip: !orderId }
  );

  // The POS opens the drawer with the id only; the number comes with the order.
  const displayNumber = orderNumber ?? order?.orderNumber;

  const currency = useRestaurantCurrency();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const userRole = currentUser?.roles?.includes(Roles.kitchen)
    ? Roles.kitchen
    : undefined;
  const { data: openSessionData } = useGetOpenSessionQuery(
    { restaurantId: currentRestaurant?.id ?? "" },
    { skip: !currentRestaurant?.id }
  );
  const hasOpenSession = !!openSessionData;

  const [changeOrderStatus, { isLoading: isChangingStatus }] =
    useChangeOrderStatusMutation();
  const confirmStatusChange = useOrderCloseConfirm();
  const { blockIfCollected, showBlocked } = useCollectedMoneyCancelBlock();

  const hasChanges = useRef(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  // Amount prefilled in the refund modal: the overpayment, or the money still
  // held when a cancel/reject was blocked (SFAPI-114).
  const [refundAmount, setRefundAmount] = useState(0);

  const { can, canAny } = usePermissions();
  // Payments are permission-gated (payments.view/capture) — cashiers, managers
  // and delegated staff (e.g. Cashier Supervisor) hold them; waiters do not.
  const canViewPayments = can("payments.view");
  const canProcessPayment = can("payments.capture");
  // A refund is a cash-register movement: the API records it on
  // POST .../movements, which requires cash_register.movement (SFRESTO-126).
  const canRecordRefund = can("cash_register.movement");
  // Editing goes through the POS, which opens on orders.manage (SFRESTO-165).
  const canUsePos = canAny(...POS_PERMISSIONS);
  // Status changes and merges need orders.manage; with orders.view only the
  // drawer is read-only (SFRESTO-184).
  const canManageOrders = canAny(...ORDERS_MANAGE_PERMISSIONS);

  const { data: paymentsData } = useGetOrderPaymentsQuery(
    { orderUuid: orderId },
    { skip: !orderId || !isOpen || !canViewPayments }
  );

  const payments = paymentsData?.results ?? order?.orderPayments ?? [];

  const canEditOrder = useMemo(() => {
    if (!order) return false;
    if (!order.canBeModified && !order.canAddItems) return false;
    // Hide editing for stale orders even if the backend still permits it.
    if (!isWithinEditWindow(order.createdAt)) return false;
    return canUsePos && !isKitchenOnly(currentUser?.roles);
  }, [order, canUsePos, currentUser?.roles]);

  // Ready/served order: editing is restricted to adding items.
  const isAddOnlyEdit = !!order && !order.canBeModified && !!order.canAddItems;

  const navigate = useNavigate();

  const handleEditOrder = () => {
    if (!order) return;
    handleClose();
    navigate(`/pos?editOrder=${encodeURIComponent(order.id)}`);
  };

  const handleStatusChange = async (
    id: string,
    newStatus: OrderStatus
  ) => {
    if (newStatus === "accepted" && !hasOpenSession) {
      toast.error(
        "Aucune session de caisse ouverte. Veuillez ouvrir une session avant d'accepter les commandes."
      );
      return;
    }
    // An order holding collected money can't be canceled/rejected until it
    // is refunded (SFAPI-114): explain it and offer the refund instead.
    const refundOptions = canRecordRefund
      ? { onRefund: openRefundModal }
      : { refundHint: "Demandez à la caisse de rembourser le client." };
    if (order && (await blockIfCollected(order, newStatus, refundOptions))) {
      return;
    }
    if (order) {
      const confirmation = await confirmStatusChange(order, newStatus);
      if (!confirmation.proceed) return;
    }
    try {
      await changeOrderStatus({ id, status: newStatus }).unwrap();
      hasChanges.current = true;
      toast.success("Statut mis à jour");
    } catch (error: any) {
      if (isCollectedMoneyViolation(error)) {
        // Our copy of the order was stale: reload it, then explain with the
        // fresh amount.
        let freshAmount: number | null = null;
        try {
          const fresh = await refetch();
          freshAmount = fresh.data ? getUnrefundedAmount(fresh.data) : null;
        } catch {
          // keep the generic wording
        }
        await showBlocked(newStatus, freshAmount, refundOptions);
        return;
      }
      if (
        hasApiErrorKey(
          error,
          "validation.order.error.status.transition_not_allowed"
        )
      ) {
        toast.error("Le statut de cette commande a changé. Rechargement...");
        refetch();
      } else {
        toastApiError(error, "Erreur lors de la modification du statut");
      }
      logger.error("OrderDetailsDrawer", "Error changing order status:", error);
    }
  };

  const openRefundModal = (amount: number) => {
    if (!hasOpenSession) {
      toast.error(
        "Aucune session de caisse ouverte. Ouvrez une session pour enregistrer le remboursement."
      );
      return;
    }
    setRefundAmount(amount);
    setIsRefundModalOpen(true);
  };

  const handleClose = () => {
    if (hasChanges.current) {
      dispatch(OrderApi.util.invalidateTags(["order-list"]));
      hasChanges.current = false;
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const totalPrice = order ? getOrderTotal(order) : 0;
  const paidAmount = order?.paidAmount ?? 0;
  const remainingAmount = order?.remainingAmount ?? totalPrice;
  // Removing items from a paid order can leave it overpaid: remainingAmount goes
  // negative while isPaid stays true. The difference is settled with a cash
  // refund movement (there's no order-level refund endpoint). remainingAmount is
  // already net of refunds, so what's still owed drives the refund button and a
  // settled overpayment can't be refunded twice (SFRESTO-114).
  const refundedAmount = order?.refundedAmount ?? 0;
  const outstandingRefund = order ? getOutstandingRefund(order) : 0;
  // An order still collectable/owed vs. one that failed. A canceled/rejected
  // order can still have collected money that must be refunded or reconciled —
  // never hide it (SFRESTO-106).
  const isActiveOrder =
    !!order && !["pending", "rejected", "canceled"].includes(order.status);
  const isFailedOrder =
    !!order && ["rejected", "canceled"].includes(order.status);
  const hasCollectedMoney = paidAmount > 0 || refundedAmount > 0;
  const collectedToReconcile = isFailedOrder
    ? Math.max(0, paidAmount - refundedAmount)
    : 0;

  return createPortal(
    <>
      {/* Backdrop */}
      <button
        className="fixed inset-0 bg-black/40 z-40 cursor-default"
        onClick={handleClose}
        aria-label="Fermer le panneau"
      />

      {/* Side panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-bold text-lg text-card-foreground">
              {/* Never fall back to a fragment of the UUID (SFRESTO-147). */}
              {displayNumber ? `Commande #${displayNumber}` : "Commande"}
            </h2>
            {order && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                <Clock className="h-3.5 w-3.5" />
                {formatDate(new Date(order.createdAt))}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Panel body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <p className="text-destructive text-sm py-4">
              Erreur lors du chargement des détails.
            </p>
          )}

          {order && (
            <div className="space-y-4">
              {/* Status badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge status={order.status}>
                  {getOrderStatusText(order.status, userRole)}
                </Badge>
                {order.isPaid && (
                  <Badge
                    variant="outline"
                    className="ring-success text-success border-success"
                  >
                    Payée
                  </Badge>
                )}
                {order.isFullyReimbursed && (
                  <Badge
                    variant="outline"
                    className="ring-amber-500 text-amber-700 border-amber-500"
                  >
                    Remboursée
                  </Badge>
                )}
              </div>

              {/* Order info grid */}
              <div className="grid grid-cols-2 gap-3">
                {order.dailySequence != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">N° du jour</p>
                    <p className="text-sm font-medium text-card-foreground tabular-nums">
                      #{order.dailySequence}
                    </p>
                  </div>
                )}
                {order.restaurantTable && (
                  <div>
                    <p className="text-xs text-muted-foreground">Table</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.restaurantTable.code}
                    </p>
                  </div>
                )}
                {order.orderType && (
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {getOrderTypeLabel(order.orderType)}
                    </p>
                  </div>
                )}
                {typeof order.metadata?.guestCount === "number" && (
                  <div>
                    <p className="text-xs text-muted-foreground">Couverts</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.metadata.guestCount}
                    </p>
                  </div>
                )}
                {order.source && (
                  <div>
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {getOrderSourceLabel(order.source)}
                    </p>
                  </div>
                )}
                {order.waiterName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Serveur</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.waiterName}
                    </p>
                  </div>
                )}
                {order.customer && (
                  <div>
                    <p className="text-xs text-muted-foreground">Client</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.customer.fullName ?? order.customer.phone}
                    </p>
                    {order.customer.fullName && order.customer.phone && (
                      <p className="text-xs text-muted-foreground">{order.customer.phone}</p>
                    )}
                  </div>
                )}
                {order.deliveryAddress && (
                  <div>
                    <p className="text-xs text-muted-foreground">Adresse de livraison</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.deliveryAddress}
                    </p>
                  </div>
                )}
                {order.deliveryFee != null && order.deliveryFee > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">Frais de livraison</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {displayPrice(order.deliveryFee, currency)}
                    </p>
                  </div>
                )}
                {order.metadata?.deliveryPersonName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Livreur</p>
                    <p className="text-sm font-medium text-card-foreground">
                      {order.metadata.deliveryPersonName}
                    </p>
                    {order.metadata.deliveryPersonPhone && (
                      <p className="text-xs text-muted-foreground">{order.metadata.deliveryPersonPhone}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Merge trace — this order was absorbed into another, or it
                  absorbed others (metadata written by the merge processor). */}
              {typeof order.metadata?.mergedInto === "string" && (
                <div className="flex items-center gap-2 rounded-lg border border-[#066a69]/30 bg-[#066a69]/5 p-3 text-[#066a69]">
                  <GitMerge className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm font-medium">
                    Fusionnée dans la commande #{order.metadata.mergedInto}
                  </p>
                </div>
              )}
              {Array.isArray(order.metadata?.mergedFrom) &&
                order.metadata.mergedFrom.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-[#066a69]/30 bg-[#066a69]/5 p-3 text-[#066a69]">
                    <GitMerge className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">
                      <span className="font-medium">
                        Regroupe {order.metadata.mergedFrom.length} commande
                        {order.metadata.mergedFrom.length > 1 ? "s" : ""}
                      </span>{" "}
                      &middot;{" "}
                      {order.metadata.mergedFrom
                        .map((n: string) => `#${n}`)
                        .join(", ")}
                    </p>
                  </div>
                )}

              {/* Dashed separator */}
              <div className="border-t border-dashed border-border" />

              {/* Order items */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-card-foreground">
                  Articles (
                  {order.orderItems.length +
                    (order.menuComboOrderItems?.length ?? 0) +
                    (order.menuFormulaOrderItems?.length ?? 0)}
                  )
                </h3>
                {order.orderItems.map((item) => (
                  <OrderItemDetail key={item.id} item={item} currency={currency} />
                ))}
                {order.menuComboOrderItems?.map((item) => (
                  <ComboOrderItem
                    key={item.id}
                    item={item}
                    currency={currency}
                  />
                ))}
                {order.menuFormulaOrderItems?.map((item) => (
                  <FormulaOrderItem
                    key={item.id}
                    item={item}
                    currency={currency}
                  />
                ))}
              </div>

              {/* Dashed separator */}
              <div className="border-t border-dashed border-border" />

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-xl font-medium">Total</span>
                <span className="text-xl font-bold text-card-foreground">
                  {formatPrice(totalPrice / 100, currency)}
                </span>
              </div>

              {/* Payment summary — shown for active orders and for any
                  canceled/rejected order that still collected money, so
                  paid-then-canceled orders never hide their payments
                  (SFRESTO-106). */}
              {(isActiveOrder || hasCollectedMoney) && (
                <>
                  <div className="border-t border-dashed border-border" />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {isActiveOrder ? "Payé" : "Encaissé"}
                      </span>
                      <span className="font-semibold text-sm text-green-600">
                        {displayPrice(paidAmount, currency)}
                      </span>
                    </div>
                    {isActiveOrder && remainingAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Restant</span>
                        <span className="font-semibold text-sm text-red-600">
                          {displayPrice(remainingAmount, currency)}
                        </span>
                      </div>
                    )}
                    {refundedAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Remboursé
                        </span>
                        <span className="font-semibold text-sm text-green-600">
                          {displayPrice(refundedAmount, currency)}
                        </span>
                      </div>
                    )}
                    {isActiveOrder && outstandingRefund > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          À rembourser
                        </span>
                        <span className="font-semibold text-sm text-amber-600">
                          {displayPrice(outstandingRefund, currency)}
                        </span>
                      </div>
                    )}
                    {/* Money collected on a canceled/rejected order that wasn't
                        refunded — surface it so it can be refunded or reconciled. */}
                    {collectedToReconcile > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          À rembourser ou rapprocher
                        </span>
                        <span className="font-semibold text-sm text-amber-600">
                          {displayPrice(collectedToReconcile, currency)}
                        </span>
                      </div>
                    )}
                    {/* Progress bar only makes sense while an order is still
                        collectable. */}
                    {isActiveOrder && (
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            paidAmount >= totalPrice ? "bg-green-500" : "bg-primary"
                          }`}
                          style={{
                            width: `${totalPrice > 0 ? Math.min((paidAmount / totalPrice) * 100, 100) : 0}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Payment history */}
                  {canViewPayments && payments.length > 0 && (
                    <PaymentHistory payments={payments} currency={currency} />
                  )}
                </>
              )}

              {/* Status history timeline */}
              {order.statusHistory && order.statusHistory.length > 0 && (
                <>
                  <div className="border-t border-dashed border-border" />
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-card-foreground">
                      Historique
                    </h3>
                    <div className="space-y-2">
                      {order.statusHistory.map((entry, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 text-sm"
                        >
                          <div className="mt-1.5 w-2 h-2 rounded-full bg-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-card-foreground">
                              {getOrderStatusText(entry.toStatus)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {entry.performedBy
                                ? entry.performedBy.fullName
                                : "Système"}{" "}
                              &middot;{" "}
                              {formatDate(new Date(entry.performedAt))}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Modification history — free-text reasons recorded on each edit
                  (and the auto-note written when orders are merged). */}
              {Array.isArray(order.metadata?.modificationHistory) &&
                order.metadata.modificationHistory.length > 0 && (
                  <>
                    <div className="border-t border-dashed border-border" />
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-card-foreground">
                        Modifications
                      </h3>
                      <div className="space-y-2">
                        {order.metadata.modificationHistory.map(
                          (
                            entry: {
                              reason?: string;
                              at?: string;
                              by?: string | null;
                            },
                            index: number,
                          ) => (
                            <div
                              key={index}
                              className="flex items-start gap-3 text-sm"
                            >
                              <PencilLine className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-card-foreground">
                                  {entry.reason}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {entry.by ?? "Système"}
                                  {entry.at && (
                                    <>
                                      {" "}
                                      &middot; {formatDate(new Date(entry.at))}
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}
            </div>
          )}
        </div>

        {/* Panel footer: actions */}
        {order && (
          <div className="px-5 py-4 border-t border-border space-y-3">
            {/* Edit order — gated on status + POS access (orders.manage) */}
            {canEditOrder && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleEditOrder}
              >
                <Pencil className="h-4 w-4 mr-2" />
                {isAddOnlyEdit ? "Ajouter des articles" : "Modifier la commande"}
              </Button>
            )}

            {/* Merge this table's other active orders into this one (today). */}
            {canManageOrders && (
              <MergeTableAction
                order={order}
                restaurantId={currentRestaurant?.id ?? ""}
                currency={currency}
                onMerged={onClose}
              />
            )}

            {/* Status transition buttons */}
            {canManageOrders &&
              order.availableTransitions &&
              order.availableTransitions.length > 0 && (
                <div className="flex gap-2">
                  {order.availableTransitions.map((transition) => (
                    <Button
                      key={transition}
                      variant="outline"
                      className={`flex-1 ${STATUS_BUTTON_STYLES[transition] ?? ""}`}
                      onClick={() =>
                        handleStatusChange(orderId, transition)
                      }
                      disabled={isChangingStatus}
                      isLoading={isChangingStatus}
                    >
                      {getOrderActionText(transition)}
                    </Button>
                  ))}
                </div>
              )}

            {/* Payment + Receipt actions */}
            {!["pending", "rejected", "canceled"].includes(order.status) && (
              <div className="flex gap-2">
                {remainingAmount > 0 && canProcessPayment && (
                  <Button
                    className="flex-1"
                    onClick={() => setIsPaymentModalOpen(true)}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Payer
                  </Button>
                )}
                {/* The receipt endpoint is payments-gated server-side, so hide
                    the button from staff who can't fetch it (e.g. waiters) to
                    avoid a dead action that 403s. */}
                {canViewPayments && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsReceiptOpen(true)}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    {remainingAmount > 0 ? "Addition" : "Reçu"}
                  </Button>
                )}
              </div>
            )}

            {/* Overpaid order — record the cash refund that settles it. Hides
                once the outstanding amount has been refunded. */}
            {outstandingRefund > 0 && canRecordRefund && (
              <Button
                variant="outline"
                className="w-full border-amber-500/40 text-amber-700 hover:bg-amber-50"
                onClick={() => openRefundModal(outstandingRefund)}
              >
                Rembourser {displayPrice(outstandingRefund, currency)}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {isPaymentModalOpen && order && (
        <PaymentModal
          order={order}
          isOpen={isPaymentModalOpen}
          onClose={() => { setIsPaymentModalOpen(false); hasChanges.current = true; }}
        />
      )}

      {/* Receipt Preview */}
      <ReceiptPreview
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        orderId={orderId}
      />

      {/* Cash refund: an overpaid order, or the money held by an order
          whose cancel/reject was blocked until it is refunded */}
      {isRefundModalOpen && order && openSessionData && currentRestaurant?.id && (
        <RecordMovementModal
          isOpen={isRefundModalOpen}
          onClose={() => {
            setIsRefundModalOpen(false);
            // Refresh so refundedAmount reflects the new movement and the
            // refund button hides once the overpayment is settled.
            refetch();
          }}
          restaurantId={currentRestaurant.id}
          sessionUuid={openSessionData.id}
          currency={currency}
          initialType="refund"
          initialAmount={refundAmount}
          orderIri={`/api/orders/${order.id}`}
          orderLabel={String(order.dailySequence ?? order.orderNumber)}
        />
      )}

    </>,
    document.body
  );
};

export default OrderDetailsDrawer;
